import * as Vec3 from 'gl-vec3';
import * as Vec2 from 'gl-vec2';

import { lerp, lerpAngle } from './lerp';

const makeGroundedState = (getFloorIntersection, getWallIntersection) => {
  const initialState = {
    groundedTime: 0.1,
    skidTime: 0,
  };

  const update = (state, dt, { movement, jump }) => {
    const lastPosition = state.position;
    state.position = Vec3.clone(state.position);
    state.velocity = Vec3.clone(state.velocity);

    // determine how fast the player wants to go and their accel
    const desiredVelocity = Vec3.create();
    const maxSpeed = Math.max(Vec3.length(state.velocity), 12);
    let maxAccel = 20 * dt;
    if (Vec2.length(movement) > 0.1) {
      state.skidTime -= dt;
      Vec3.set(desiredVelocity, movement[0], 0, movement[1]);
      if (Vec3.length(state.velocity) > 3 && Vec3.angle(desiredVelocity, state.velocity) > 0.45 * Math.PI) {
        state.skidTime = 0.25;
        maxAccel = 40 * dt;
      }
      const targetFacing = Math.atan2(movement[0], movement[1]);
      state.facing = lerpAngle(state.facing, targetFacing, 0.1, dt*60);
    } else {
      maxAccel = 30 * dt;
      state.skidTime = 0;
    }
    Vec3.scale(desiredVelocity, desiredVelocity, maxSpeed);

    // accel towards the desired velocity without moving past it
    if (Vec3.distance(state.velocity, desiredVelocity) > maxAccel) {
      const accel = Vec3.subtract([], desiredVelocity, state.velocity);
      Vec3.normalize(accel, accel);
      Vec3.scaleAndAdd(state.velocity, state.velocity, accel, maxAccel);
    } else {
      Vec3.copy(state.velocity, desiredVelocity);
    }
    if (Vec3.length(state.velocity) > maxSpeed) {
      Vec3.normalize(state.velocity, state.velocity);
      Vec3.scale(state.velocity, state.velocity, maxSpeed);
    }

    // walk animation
    state.walk += Vec3.length(state.velocity) * dt;
    state.walkScale = lerp(state.walkScale, Vec3.length(state.velocity) / 10, 0.3, dt*60);
    
    state.velocity[1] = 0;
    Vec3.scaleAndAdd(state.position, state.position, state.velocity, dt);
    
    const wall = getWallIntersection(Vec3.add([], state.position, [0, 2, 0]), 0.5);
    if (wall.hit && wall.distance < 1) {
      Vec3.scaleAndAdd(state.position, state.position, wall.normal, 1-wall.distance);
      const scale = Math.max(-Math.cos(Vec3.angle(state.velocity, wall.normal)), 0);
      Vec3.scaleAndAdd(state.velocity, state.velocity, wall.normal, Vec3.length(state.velocity) * scale);
      const normal2d = Vec2.fromValues(wall.normal[0], wall.normal[2]);
      if (Vec2.dot(movement, normal2d) < -0.95) {
        Vec3.set(state.velocity, 0, 0, 0);
        Vec3.copy(state.position, lastPosition);
      }
    }
    
    const floor = getFloorIntersection(state.position, 1.5);
    if (floor.hit) {
      if (floor.distance < 1.5) {
        state.groundedTime = Math.max(0.1, dt);
        Vec3.copy(state.position, floor.hit)
      }
      state.shadowNormal = floor.normal;
      state.shadowDistance = state.position[1] - floor.hit[1];
    } else {
      Vec3.copy(state.position, lastPosition);
      Vec3.set(state.velocity, 0, 0, 0);
    }
    
    state.groundedTime -= dt;
    if (state.groundedTime < 0) {
      state.mode = 'jumping';
    }

    // jumping
    if (!state.jumped && jump) {
      state.skidTime = 0;
      if (state.skidTime > 0) {
        state.mode = 'skidjumping';
        Vec3.set(state.velocity, 0, 25, 0);
      } else {
        state.mode = 'jumping';
        state.velocity[1] = 13 + 0.2 * Vec2.length([state.velocity[0], state.velocity[2]]);
      }
      state.jumped = true;
    } else if (!jump) {
      state.jumped = false;
    }
    
    return state;
  };

  return { initialState, update };
};

export default makeGroundedState;